import React, { useEffect, useRef, useState } from 'react';
import { Bot, BarChart3, LineChart as LineChartIcon, PieChart, Send, X, Loader2, Minimize2, Maximize2, Sparkles, Mic, MicOff, History, Plus, Trash2, MessageSquare } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart as RechartsPieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { motion, AnimatePresence, useDragControls } from 'motion/react';
import { cn } from '../lib/utils';

type ChartPayload = {
  type: 'bar' | 'line' | 'pie';
  title: string;
  data: Array<{ label: string; value: number }>;
};

type ChatMessage = {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  chart?: ChartPayload | null;
};

type ConversationSummary = {
  id: string;
  title: string;
  updatedAt: string;
  messageCount: number;
};

interface AIChatBotProps {
  orgId: string;
  orgName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const token = () => typeof window === 'undefined' ? null : localStorage.getItem('app_auth_token');

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  [index: number]: { transcript: string };
};

type SpeechRecognitionEventLike = Event & {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionInstance = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

function ChartResult({ chart }: { chart: ChartPayload }) {
  const data = Array.isArray(chart.data) ? chart.data.filter(item => Number.isFinite(Number(item.value))).slice(0, 24) : [];
  if (!data.length) return null;
  const colors = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#7c3aed', '#0891b2'];
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950/40">
      <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {chart.type === 'bar' ? <BarChart3 className="h-3.5 w-3.5" /> : chart.type === 'line' ? <LineChartIcon className="h-3.5 w-3.5" /> : <PieChart className="h-3.5 w-3.5" />}
        <span>{chart.title || 'Chart'}</span>
      </div>
      <div className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} debounce={50}>
          {chart.type === 'pie' ? (
            <RechartsPieChart>
              <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={62} innerRadius={28} paddingAngle={2}>
                {data.map((entry, index) => <Cell key={`${entry.label}-${index}`} fill={colors[index % colors.length]} />)}
              </Pie>
              <Tooltip formatter={(value: number) => value.toLocaleString()} />
            </RechartsPieChart>
          ) : chart.type === 'line' ? (
            <LineChart data={data} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#cbd5e1" />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#cbd5e1" />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AIChatBot({ orgId, orgName, isOpen, onClose }: AIChatBotProps) {
  const dragControls = useDragControls();
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isOpen) {
      setIsMinimized(false);
      if (isListening) {
        recognitionRef.current?.stop();
        setIsListening(false);
      }
    }
  }, [isOpen, isListening]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  });

  const loadHistory = async (selectLatest = false) => {
    if (!orgId) return;
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/ai/conversations?orgId=${encodeURIComponent(orgId)}`, { headers: authHeaders() });
      const payload = await response.json().catch(() => []);
      if (!response.ok) throw new Error(payload?.error || 'Could not load chat history');
      const nextConversations = Array.isArray(payload) ? payload : [];
      setConversations(nextConversations);
      if (selectLatest && !conversationId && nextConversations[0]) {
        await loadConversation(nextConversations[0].id);
      }
    } catch (error) {
      console.error('Could not load AI chat history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadConversation = async (id: string) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`/api/ai/conversations/${encodeURIComponent(id)}?orgId=${encodeURIComponent(orgId)}`, { headers: authHeaders() });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'Could not load conversation');
      setConversationId(payload.id);
      setMessages(Array.isArray(payload.messages) ? payload.messages.map((message: ChatMessage) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        chart: message.chart || null,
      })) : []);
      setInput('');
      setHistoryOpen(false);
    } catch (error: any) {
      setMessages([{ role: 'assistant', content: error?.message || 'Could not load conversation history.' }]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setInput('');
    setHistoryOpen(false);
  };

  const deleteConversation = async (id: string) => {
    try {
      const response = await fetch(`/api/ai/conversations/${encodeURIComponent(id)}?orgId=${encodeURIComponent(orgId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'Could not delete conversation');
      setConversations(current => current.filter(conversation => conversation.id !== id));
      if (conversationId === id) startNewConversation();
    } catch (error) {
      console.error('Could not delete AI conversation:', error);
    }
  };

  useEffect(() => {
    setMessages([]);
    setInput('');
    setConversationId(null);
    setHistoryOpen(false);
    setConversations([]);
  }, [orgId]);

  useEffect(() => {
    if (isOpen && !conversationId) void loadHistory(true);
  }, [isOpen, orgId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const Recognition = ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition) as SpeechRecognitionConstructor | undefined;
    if (!Recognition) return;

    const recognition = new Recognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = document.documentElement.lang || navigator.language || 'en-US';
    recognition.onresult = event => {
      const transcript = Array.from({ length: event.results.length }, (_, index) => event.results[index])
        .filter(result => result.isFinal)
        .map(result => result[0]?.transcript || '')
        .join(' ')
        .trim();
      if (transcript) {
        setInput(current => `${current.trim()}${current.trim() ? ' ' : ''}${transcript}`);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    setSpeechSupported(true);

    return () => {
      recognition.stop();
      recognitionRef.current = null;
    };
  }, []);

  const toggleVoiceInput = () => {
    const recognition = recognitionRef.current;
    if (!recognition || isLoading) return;
    if (isListening) {
      recognition.stop();
      setIsListening(false);
      return;
    }
    try {
      recognition.start();
      setIsListening(true);
    } catch {
      setIsListening(false);
    }
  };

  const handleSend = async () => {
    const question = input.trim();
    if (!question || isLoading || !orgId) return;
    const userMessage: ChatMessage = { role: 'user', content: question };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token() ? { Authorization: `Bearer ${token()}` } : {}) },
        body: JSON.stringify({
          orgId,
          conversationId,
          messages: [{ role: 'user', content: question }],
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'AI request failed');
      setConversationId(payload.conversationId || conversationId);
      setMessages(current => [...current, { role: 'assistant', content: payload.answer || 'No answer was returned.', chart: payload.chart }]);
      void loadHistory();
    } catch (error: any) {
      setMessages(current => [...current, { role: 'assistant', content: error?.message || 'The AI request failed. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          drag
          dragControls={dragControls}
          dragListener={false}
          dragMomentum={false}
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          className="ai-assistant-panel fixed bottom-6 end-6 z-[9999] flex flex-col items-end touch-none print:hidden"
        >
          <div className={cn('flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900', isMinimized ? 'ai-assistant-surface h-16 w-80' : 'ai-assistant-surface h-[min(680px,calc(100vh-3rem))] w-[440px]')}>
            <div onPointerDown={event => dragControls.start(event)} className="flex cursor-grab items-center justify-between bg-slate-950 px-4 py-3 text-white active:cursor-grabbing">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10"><Sparkles className="h-4 w-4 text-white" /></div>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-bold">Organization AI</h3>
                  <p className="truncate text-[10px] text-slate-300">Only: {orgName || 'active organization'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" aria-label="Open chat history" onPointerDown={event => event.stopPropagation()} onClick={() => { setHistoryOpen(value => !value); if (!historyOpen) void loadHistory(); }} className={cn('rounded-lg p-2 hover:bg-white/10', historyOpen && 'bg-white/10')}><History className="h-4 w-4" /></button>
                <button type="button" aria-label="Start new conversation" onPointerDown={event => event.stopPropagation()} onClick={startNewConversation} className="rounded-lg p-2 hover:bg-white/10"><Plus className="h-4 w-4" /></button>
                <button type="button" aria-label={isMinimized ? 'Expand assistant' : 'Minimize assistant'} onClick={() => setIsMinimized(value => !value)} className="rounded-lg p-2 hover:bg-white/10">{isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}</button>
                <button type="button" aria-label="Close assistant" onClick={onClose} className="rounded-lg p-2 hover:bg-white/10"><X className="h-4 w-4" /></button>
              </div>
            </div>
            {!isMinimized && (
              <>
                {historyOpen ? (
                  <div className="flex-1 overflow-y-auto bg-slate-50 p-4 dark:bg-slate-900/60">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">Your chat history</h4>
                        <p className="mt-1 text-[10px] text-slate-500">Private to you in {orgName || 'this organization'}</p>
                      </div>
                      <button type="button" onClick={startNewConversation} className="flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-2 text-[10px] font-bold text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"><Plus className="h-3 w-3" /> New chat</button>
                    </div>
                    {historyLoading && <div className="flex items-center gap-2 py-6 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading history…</div>}
                    {!historyLoading && !conversations.length && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-5 text-center text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800/50">No saved conversations yet.</div>}
                    <div className="space-y-2">
                      {!historyLoading && conversations.map(conversation => (
                        <div key={conversation.id} className={cn('group flex items-start gap-2 rounded-xl border bg-white p-3 transition dark:bg-slate-800', conversation.id === conversationId ? 'border-slate-500 ring-1 ring-slate-500/20' : 'border-slate-200 hover:border-slate-400 dark:border-slate-700')}>
                          <button type="button" onClick={() => void loadConversation(conversation.id)} className="min-w-0 flex-1 text-left">
                            <span className="flex items-center gap-2 text-xs font-semibold text-slate-800 dark:text-slate-100"><MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{conversation.title}</span></span>
                            <span className="mt-1 block pl-5 text-[10px] text-slate-400">{conversation.messageCount} messages · {new Date(conversation.updatedAt).toLocaleString()}</span>
                          </button>
                          <button type="button" aria-label={`Delete ${conversation.title}`} title="Delete conversation" onClick={() => void deleteConversation(conversation.id)} className="rounded-lg p-1.5 text-slate-400 opacity-70 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <div className="flex-1 overflow-y-auto bg-slate-50 p-4 dark:bg-slate-900/60">
                  <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    I can summarize, find records, calculate financial information, and create charts from <strong>{orgName || 'the active organization'}</strong> only.
                  </div>
                  {messages.map((message, index) => (
                    <div key={`${message.role}-${index}`} className={cn('mb-4 flex flex-col', message.role === 'user' ? 'items-end' : 'items-start')}>
                      <div className={cn('max-w-[90%] rounded-2xl px-3.5 py-3 text-xs leading-relaxed', message.role === 'user' ? 'rounded-br-sm bg-slate-900 text-white' : 'rounded-bl-sm border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100')}>
                        <div className="whitespace-pre-wrap">{message.content}</div>
                        {message.chart && <ChartResult chart={message.chart} />}
                      </div>
                      <span className="mt-1 px-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">{message.role === 'user' ? 'You' : 'Organization AI'}</span>
                    </div>
                  ))}
                  {isLoading && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />Reading organization data…</div>}
                  <div ref={messagesEndRef} />
                </div>}
                <div className="border-t border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                  <div className="flex items-end gap-2">
                    <textarea aria-label="Ask organization AI" value={input} onChange={event => setInput(event.target.value)} onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void handleSend(); } }} rows={2} placeholder={isListening ? 'Listening…' : 'Ask about revenue, invoices, stock…'} className="min-h-11 flex-1 resize-none rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 dark:border-slate-600 dark:bg-slate-800" />
                    {speechSupported && <button type="button" aria-label={isListening ? 'Stop voice input' : 'Start voice input'} aria-pressed={isListening} title={isListening ? 'Stop voice input' : 'Type with your voice'} onClick={toggleVoiceInput} disabled={isLoading} className={cn('rounded-xl p-3 transition focus:outline-none focus:ring-2 focus:ring-blue-600/30 disabled:cursor-not-allowed disabled:opacity-40', isListening ? 'bg-rose-600 text-white animate-pulse hover:bg-rose-700' : 'border border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')}>
                      {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                    </button>}
                    <button type="button" aria-label="Send question" onClick={() => void handleSend()} disabled={!input.trim() || isLoading || !orgId} className="rounded-lg bg-slate-900 p-3 text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"><Send className="h-4 w-4" /></button>
                  </div>
                  <p className="mt-2 flex items-center gap-1 text-[9px] text-slate-400"><Bot className="h-3 w-3" /> Data is filtered on the server by the active organization.{isListening && <span className="font-semibold text-rose-500"> Listening…</span>}</p>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
